/**
 * VitalSparkline — a minimal, dependency-free trend line. skypoint-ui ships NO chart/sparkline
 * component (verified against vendor/skypoint-ui/dist/index.d.ts), so we render a tokenized inline SVG
 * polyline instead of pulling in a chart lib. Stroke colors reference design-system CSS vars
 * (var(--color-*)) — never raw hex. Supports a dual series (blood pressure: systolic + diastolic), each
 * series carrying a text label so the chart is never color-only. The caller only passes series with
 * >= 2 points; free-text/value_string rows are never trended.
 */

export interface SparkSeries {
  /** Points in chronological (oldest -> newest) order. */
  values: number[];
  /** A design-system color token, e.g. "var(--color-brand-80)". */
  colorVar: string;
  /** Visible legend label (status/identity by label, never color alone). */
  label: string;
}

const W = 180;
const H = 44;
const PAD = 4;

function pointsFor(values: number[], min: number, span: number): string {
  const n = values.length;
  return values
    .map((v, i) => {
      const x = n === 1 ? W / 2 : PAD + (i * (W - 2 * PAD)) / (n - 1);
      const y = PAD + (1 - (v - min) / span) * (H - 2 * PAD);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
}

export function VitalSparkline({ series, unit }: { series: SparkSeries[]; unit?: string | null }) {
  const all = series.flatMap((s) => s.values);
  const min = Math.min(...all);
  const max = Math.max(...all);
  const span = max - min || 1; // flat line guard

  return (
    <div className="flex flex-col gap-1">
      <svg
        width={W}
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`Trend of ${series.map((s) => s.label).join(" and ")}${unit ? ` in ${unit}` : ""}`}
        className="overflow-visible"
      >
        {series.map((s) => (
          <polyline
            key={s.label}
            points={pointsFor(s.values, min, span)}
            fill="none"
            stroke={s.colorVar}
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}
      </svg>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5">
        {series.map((s) => (
          <span key={s.label} className="inline-flex items-center gap-1 text-xs text-neutral-500">
            <span
              aria-hidden
              className="inline-block h-2 w-2 rounded-full"
              style={{ background: s.colorVar }}
            />
            {s.label}: {s.values[s.values.length - 1]}
            {unit ? ` ${unit}` : ""}
          </span>
        ))}
      </div>
    </div>
  );
}
